import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { CreatePatientRequest, Patient } from '@yui-receipt/shared'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'

/**
 * New-patient form. Consent checkboxes are PDPA-mandated — the BE will
 * reject the create if either is missing, and we mirror that here so the
 * therapist gets immediate feedback instead of a round-trip 400.
 */
export default function PatientNewPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<CreatePatientRequest>({
    hn: '',
    nameTh: '',
    nameEn: null,
    dob: null,
    sex: null,
    phone: null,
    emergencyContact: null,
    heightCm: null,
    weightKg: null,
    pmh: null,
    allergies: null,
    occupation: null,
    address: null,
    consentAudio: false,
    consentAiScribe: false,
  })

  const createMut = useMutation({
    mutationFn: (body: CreatePatientRequest) =>
      api<{ patient: Patient }>('/patients', { method: 'POST', body }),
    onSuccess: (res) => {
      toastSuccess('สร้างผู้ป่วยสำเร็จ')
      qc.invalidateQueries({ queryKey: ['patients'] })
      navigate(`/patients/${res.patient.id}`, { replace: true })
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiClientError ? err.message : 'สร้างไม่สำเร็จ'
      toastError(msg)
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.hn.trim() || !form.nameTh.trim()) {
      toastError('กรุณากรอก HN และชื่อ')
      return
    }
    if (!form.consentAudio || !form.consentAiScribe) {
      toastError('ต้องได้รับความยินยอมจากผู้ป่วยก่อนบันทึก (PDPA)')
      return
    }
    createMut.mutate({
      ...form,
      hn: form.hn.trim(),
      nameTh: form.nameTh.trim(),
      nameEn: form.nameEn?.trim() || null,
      phone: form.phone?.trim() || null,
      emergencyContact: form.emergencyContact?.trim() || null,
      pmh: form.pmh?.trim() || null,
      allergies: form.allergies?.trim() || null,
      occupation: form.occupation?.trim() || null,
      address: form.address?.trim() || null,
    })
  }

  function set<K extends keyof CreatePatientRequest>(k: K, v: CreatePatientRequest[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">เพิ่มผู้ป่วย</h1>
          <Link to="/patients" className="text-sm text-slate-500 hover:text-brand-600">
            ← กลับ
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="hn">HN *</label>
            <input
              id="hn"
              className="input"
              value={form.hn}
              onChange={(e) => set('hn', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="nameTh">ชื่อ (ไทย) *</label>
            <input
              id="nameTh"
              className="input"
              value={form.nameTh}
              onChange={(e) => set('nameTh', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="nameEn">ชื่อ (อังกฤษ)</label>
            <input
              id="nameEn"
              className="input"
              value={form.nameEn ?? ''}
              onChange={(e) => set('nameEn', e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="phone">โทรศัพท์</label>
            <input
              id="phone"
              className="input"
              type="tel"
              inputMode="tel"
              value={form.phone ?? ''}
              onChange={(e) => set('phone', e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="dob">วันเกิด</label>
            <input
              id="dob"
              className="input"
              type="date"
              value={form.dob ?? ''}
              onChange={(e) => set('dob', e.target.value || null)}
            />
          </div>
          <div>
            <label className="label" htmlFor="sex">เพศ</label>
            <select
              id="sex"
              className="input"
              value={form.sex ?? ''}
              onChange={(e) => set('sex', (e.target.value as 'M' | 'F') || null)}
            >
              <option value="">— ไม่ระบุ —</option>
              <option value="M">ชาย</option>
              <option value="F">หญิง</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="heightCm">ส่วนสูง (ซม)</label>
            <input
              id="heightCm"
              className="input"
              type="number"
              inputMode="decimal"
              value={form.heightCm ?? ''}
              onChange={(e) =>
                set('heightCm', e.target.value ? Number(e.target.value) : null)
              }
            />
          </div>
          <div>
            <label className="label" htmlFor="weightKg">น้ำหนัก (กก)</label>
            <input
              id="weightKg"
              className="input"
              type="number"
              inputMode="decimal"
              value={form.weightKg ?? ''}
              onChange={(e) =>
                set('weightKg', e.target.value ? Number(e.target.value) : null)
              }
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label" htmlFor="emergencyContact">ผู้ติดต่อฉุกเฉิน</label>
            <input
              id="emergencyContact"
              className="input"
              value={form.emergencyContact ?? ''}
              onChange={(e) => set('emergencyContact', e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label" htmlFor="pmh">โรคประจำตัว</label>
            <textarea
              id="pmh"
              className="input min-h-[60px]"
              value={form.pmh ?? ''}
              onChange={(e) => set('pmh', e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label" htmlFor="allergies">ประวัติแพ้ยา/อาหาร</label>
            <input
              id="allergies"
              className="input"
              value={form.allergies ?? ''}
              onChange={(e) => set('allergies', e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="occupation">อาชีพ</label>
            <input
              id="occupation"
              className="input"
              value={form.occupation ?? ''}
              onChange={(e) => set('occupation', e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label" htmlFor="address">ที่อยู่</label>
            <textarea
              id="address"
              className="input min-h-[60px]"
              value={form.address ?? ''}
              onChange={(e) => set('address', e.target.value)}
            />
          </div>
        </div>

        <fieldset className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
          <legend className="px-1 text-sm font-semibold text-amber-900">
            ความยินยอม PDPA (จำเป็น)
          </legend>
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 accent-brand-600"
              checked={form.consentAudio}
              onChange={(e) => set('consentAudio', e.target.checked)}
            />
            <span className="text-sm text-slate-800">
              ผู้ป่วยยินยอมให้บันทึกเสียงระหว่างตรวจ (เก็บ 90 วันแล้วลบอัตโนมัติ)
            </span>
          </label>
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 accent-brand-600"
              checked={form.consentAiScribe}
              onChange={(e) => set('consentAiScribe', e.target.checked)}
            />
            <span className="text-sm text-slate-800">
              ผู้ป่วยยินยอมให้ใช้ AI ช่วยถอดเสียงและสร้างโน้ต
              (ผู้เชี่ยวชาญตรวจและรับรองก่อนบันทึกเป็นทางการ)
            </span>
          </label>
        </fieldset>

        <div className="flex gap-2">
          <Link to="/patients" className="btn-ghost flex-1 text-center min-h-tap">
            ยกเลิก
          </Link>
          <button
            type="submit"
            className="btn-primary flex-1 min-h-tap disabled:opacity-50"
            disabled={createMut.isPending}
          >
            {createMut.isPending ? 'กำลังบันทึก…' : '✓ บันทึก'}
          </button>
        </div>
      </form>
    </div>
  )
}
